import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { InsightsBlock, ReportData } from '../tokens/types';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';

type InsightsKey = 'overview' | 'compliance' | 'legalChecks' | 'auditedFinancials' | 'relatedParty';

type Kind = 'OBSERVATION' | 'RECOMMENDATION' | 'RISK';

type Stream = { kind: Kind; text: string };

const KIND_META: Record<Kind, { label: string; accent: string; tint: string; icon: string }> = {
    OBSERVATION: { label: 'OBS', accent: colors.info, tint: colors.infoSoft, icon: '◆' },
    RECOMMENDATION: { label: 'REC', accent: colors.brand, tint: colors.brandSoft, icon: '▲' },
    RISK: { label: 'RISK', accent: colors.neg, tint: colors.negSoft, icon: '●' },
};

export const getInsights = (data: ReportData, key: InsightsKey): InsightsBlock | undefined => data.insights?.[key];

const isNonEmpty = (b?: InsightsBlock): b is InsightsBlock => !!b && b.observations.length + b.recommendations.length + b.risks.length > 0;

const interleave = (b: InsightsBlock, max: number): Stream[] => {
    const obs: Stream[] = b.observations.map((t) => ({ kind: 'OBSERVATION' as const, text: t }));
    const rec: Stream[] = b.recommendations.map((t) => ({ kind: 'RECOMMENDATION' as const, text: t }));
    const risks: Stream[] = b.risks.map((t) => ({ kind: 'RISK' as const, text: t }));
    // Lead with risks, then observations, then recs — surfaces the most decision-relevant items first.
    return [...risks, ...obs, ...rec].slice(0, max);
};

const tally = (b: InsightsBlock) => ({
    OBSERVATION: b.observations.length,
    RECOMMENDATION: b.recommendations.length,
    RISK: b.risks.length,
});

// ──────────────────────────── PDF ────────────────────────────

export const PdfInlineInsights: React.FC<{ data: ReportData; k: InsightsKey; max?: number }> = ({ data, k, max = 8 }) => {
    const b = getInsights(data, k);
    if (!isNonEmpty(b)) return null;
    const counts = tally(b);
    const items = interleave(b, max);
    return (
        <View style={{ marginTop: space.lg }} minPresenceAhead={120}>
            <View
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 6,
                }}
                wrap={false}
            >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            letterSpacing: 1.2,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.sansBold,
                            color: colors.brand,
                        }}
                    >
                        Analyst Briefing
                    </Text>
                    {(['RISK', 'OBSERVATION', 'RECOMMENDATION'] as Kind[]).map((kind) => {
                        const n = counts[kind];
                        if (n === 0) return null;
                        const meta = KIND_META[kind];
                        // Three-part chip centering (skill rule 9): tight lineHeight + center
                        // alignment on the View + asymmetric paddingTop/paddingBottom.
                        return (
                            <View
                                key={kind}
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 3,
                                    paddingHorizontal: 5,
                                    paddingTop: 2,
                                    paddingBottom: 3,
                                    borderRadius: 3,
                                    backgroundColor: meta.tint,
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 7,
                                        color: meta.accent,
                                        fontFamily: fontFamily.sansBold,
                                        lineHeight: 1,
                                    }}
                                >
                                    {meta.label}
                                </Text>
                                <Text
                                    style={{
                                        fontSize: 8,
                                        color: meta.accent,
                                        fontFamily: fontFamily.sansBold,
                                        lineHeight: 1,
                                    }}
                                >
                                    {n}
                                </Text>
                            </View>
                        );
                    })}
                </View>
                <Text style={{ fontSize: typeScale.micro, color: colors.mutedSoft }}>{b.createdAt ? `Generated ${b.createdAt}` : ''}</Text>
            </View>
            <View
                style={{
                    borderLeftWidth: 2,
                    borderLeftColor: colors.brand,
                    paddingLeft: space.sm,
                    paddingVertical: 4,
                }}
            >
                {items.map((it, i) => {
                    const meta = KIND_META[it.kind];
                    return (
                        <View key={i} style={{ flexDirection: 'row', marginBottom: 6 }} wrap={false}>
                            {/* OBS/REC/RISK kind tag — three-part centering pattern. */}
                            <View
                                style={{
                                    width: 30,
                                    paddingTop: 2,
                                    paddingBottom: 3,
                                    paddingHorizontal: 3,
                                    backgroundColor: meta.tint,
                                    borderRadius: 2,
                                    marginRight: 6,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 7,
                                        color: meta.accent,
                                        fontFamily: fontFamily.sansBold,
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                    }}
                                >
                                    {meta.label}
                                </Text>
                            </View>
                            <Text style={{ flex: 1, fontSize: typeScale.caption, color: colors.inkSoft, lineHeight: 1.45 }}>{it.text}</Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

// ──────────────────────────── Screen ────────────────────────────

export const ScreenInlineInsights: React.FC<{ data: ReportData; k: InsightsKey; max?: number }> = ({ data, k, max = 8 }) => {
    const b = getInsights(data, k);
    if (!isNonEmpty(b)) return null;
    const counts = tally(b);
    const items = interleave(b, max);
    return (
        <div
            style={{
                marginTop: 18,
                padding: 14,
                borderRadius: 8,
                background: 'linear-gradient(180deg, rgba(96,33,179,0.04) 0%, rgba(96,33,179,0.0) 100%)',
                border: `1px solid ${colors.hairlineSoft}`,
                borderLeft: `3px solid ${colors.brand}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                    flexWrap: 'wrap',
                    gap: 8,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                        style={{
                            fontSize: 11,
                            letterSpacing: 1.4,
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            color: colors.brand,
                        }}
                    >
                        Analyst Briefing
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {(['RISK', 'OBSERVATION', 'RECOMMENDATION'] as Kind[]).map((kind) => {
                            const n = counts[kind];
                            if (n === 0) return null;
                            const meta = KIND_META[kind];
                            return (
                                <span
                                    key={kind}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        background: meta.tint,
                                        color: meta.accent,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                    }}
                                >
                                    <span>{meta.label}</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                                </span>
                            );
                        })}
                    </div>
                </div>
                <span style={{ fontSize: 10, color: colors.mutedSoft }}>{b.createdAt ? `Generated ${b.createdAt}` : ''}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map((it, i) => {
                    const meta = KIND_META[it.kind];
                    return (
                        <div
                            key={i}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '52px 1fr',
                                alignItems: 'flex-start',
                                gap: 10,
                                padding: '6px 0',
                                borderBottom: i === items.length - 1 ? 'none' : `1px dashed ${colors.hairlineSoft}`,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 9,
                                    letterSpacing: 0.6,
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                    color: meta.accent,
                                    background: meta.tint,
                                    borderRadius: 4,
                                    padding: '3px 6px',
                                    textAlign: 'center',
                                    alignSelf: 'flex-start',
                                    marginTop: 1,
                                }}
                            >
                                {meta.label}
                            </span>
                            <span style={{ fontSize: 12.5, color: colors.inkSoft, lineHeight: 1.5 }}>{it.text}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
